import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Mail, Key, Lock, ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Account-type configuration: endpoint prefix, brand, and return-to-login path
const ACCOUNT_CONFIG = {
  user: {
    prefix: '/auth',
    brand: 'EvalentX',
    loginPath: '/login',
    accent: 'red',
  },
  student: {
    prefix: '/codearena',
    brand: 'EvalentX Arena',
    loginPath: '/codearena/auth',
    accent: 'blue',
  },
  company: {
    prefix: '/company',
    brand: 'EvalentX for Companies',
    loginPath: '/company/auth',
    accent: 'purple',
  },
};

const ACCENT_CLASSES = {
  red: {
    header: 'bg-gradient-to-r from-red-600 to-red-700',
    headerSub: 'text-red-200',
    btn: 'bg-red-600 hover:bg-red-700',
    step: 'bg-red-600',
    focus: 'focus:border-red-500 focus:ring-red-200',
    link: 'text-red-600 hover:text-red-800',
    linkBold: 'text-red-500 hover:text-red-400',
  },
  blue: {
    header: 'bg-gradient-to-r from-[#0a66c2] to-[#004182]',
    headerSub: 'text-blue-100',
    btn: 'bg-[#0a66c2] hover:bg-[#004182]',
    step: 'bg-[#0a66c2]',
    focus: 'focus:border-[#0a66c2] focus:ring-blue-200',
    link: 'text-[#0a66c2] hover:text-[#004182]',
    linkBold: 'text-[#0a66c2] hover:text-[#004182]',
  },
  purple: {
    header: 'bg-gradient-to-r from-purple-600 to-indigo-600',
    headerSub: 'text-purple-200',
    btn: 'bg-purple-600 hover:bg-purple-700',
    step: 'bg-purple-600',
    focus: 'focus:border-purple-500 focus:ring-purple-200',
    link: 'text-purple-600 hover:text-purple-800',
    linkBold: 'text-purple-500 hover:text-purple-400',
  },
};

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const accountType = ACCOUNT_CONFIG[params.accountType] ? params.accountType : 'user';
  const config = ACCOUNT_CONFIG[accountType];
  const accent = ACCENT_CLASSES[config.accent];

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
      setOtp(newOtp);
    }
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await axios.post(`${API}${config.prefix}/forgot-password`, { email });
      setSuccess('If an account with this email exists, you will receive an OTP shortly. Please check your inbox.');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}${config.prefix}/verify-otp`, { email, otp: otpString });
      setSuccess('OTP verified successfully! Please set your new password.');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}${config.prefix}/reset-password`, {
        email,
        otp: otp.join(''),
        new_password: newPassword,
      });
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => navigate(config.loginPath), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
      <div className="max-w-md w-full">
        <Link
          to={config.loginPath}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          data-testid="back-to-login-link"
        >
          <ArrowLeft size={20} />
          Back to Login
        </Link>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className={`${accent.header} px-8 py-6`}>
            <h1 className="text-2xl font-bold text-white">Reset Password</h1>
            <p className={`${accent.headerSub} mt-1`}>
              {step === 1 && `Enter your ${config.brand} email to receive an OTP`}
              {step === 2 && 'Enter the 6-digit OTP sent to your email'}
              {step === 3 && 'Create your new password'}
            </p>
          </div>

          <div className="px-8 py-4 bg-gray-50 border-b">
            <div className="flex items-center justify-between">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                    step >= s ? `${accent.step} text-white` : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > s ? <CheckCircle size={18} /> : s}
                  </div>
                  <span className={`ml-2 text-sm hidden sm:block ${
                    step >= s ? 'text-gray-800 font-medium' : 'text-gray-400'
                  }`}>
                    {s === 1 && 'Email'}
                    {s === 2 && 'Verify OTP'}
                    {s === 3 && 'New Password'}
                  </span>
                  {s < 3 && (
                    <div className={`w-12 h-1 mx-3 rounded ${
                      step > s ? accent.step : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="px-8 py-8">
            {error && (
              <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="forgot-error">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
            {success && (
              <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-start gap-3" data-testid="forgot-success">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-green-700 text-sm">{success}</p>
              </div>
            )}

            {step === 1 && (
              <form onSubmit={handleRequestOTP} className="space-y-6" data-testid="forgot-step-email">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your registered email"
                      className={`w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 transition-all ${accent.focus}`}
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">We'll send a 6-digit OTP to this email address</p>
                </div>
                <button
                  type="submit"
                  disabled={loading || !email}
                  className={`w-full ${accent.btn} text-white py-3 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all`}
                  data-testid="forgot-send-otp-btn"
                >
                  {loading ? (<><Loader2 className="animate-spin" size={20} />Sending OTP...</>) : (<><Mail size={20} />Send OTP</>)}
                </button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleVerifyOTP} className="space-y-6" data-testid="forgot-step-otp">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 text-center">Enter 6-Digit OTP</label>
                  <div className="flex justify-center gap-2 sm:gap-3">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ''))}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={handleOtpPaste}
                        className={`w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 transition-all ${accent.focus}`}
                        data-testid={`forgot-otp-${index}`}
                      />
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-gray-500 text-center">
                    OTP sent to <strong>{email}</strong>
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setStep(1); setOtp(['', '', '', '', '', '']); setError(''); setSuccess(''); }}
                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all"
                    data-testid="forgot-change-email-btn"
                  >
                    Change Email
                  </button>
                  <button
                    type="submit"
                    disabled={loading || otp.join('').length !== 6}
                    className={`flex-1 ${accent.btn} text-white py-3 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all`}
                    data-testid="forgot-verify-otp-btn"
                  >
                    {loading ? (<><Loader2 className="animate-spin" size={20} />Verifying...</>) : (<><Key size={20} />Verify OTP</>)}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleRequestOTP}
                  disabled={loading}
                  className={`w-full ${accent.link} text-sm font-medium transition-colors`}
                  data-testid="forgot-resend-otp-btn"
                >
                  Didn't receive OTP? Resend
                </button>
              </form>
            )}

            {step === 3 && (
              <form onSubmit={handleResetPassword} className="space-y-6" data-testid="forgot-step-password">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className={`w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 transition-all ${accent.focus}`}
                      required
                      minLength={6}
                      data-testid="forgot-new-password"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:ring-2 transition-all ${
                        confirmPassword && newPassword !== confirmPassword
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                          : confirmPassword && newPassword === confirmPassword
                            ? 'border-green-300 focus:border-green-500 focus:ring-green-200'
                            : `border-gray-300 ${accent.focus}`
                      }`}
                      required
                      data-testid="forgot-confirm-password"
                    />
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="mt-1 text-xs text-red-600">Passwords do not match</p>
                  )}
                  {confirmPassword && newPassword === confirmPassword && (
                    <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle size={12} /> Passwords match
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={loading || newPassword.length < 6 || newPassword !== confirmPassword}
                  className={`w-full ${accent.btn} text-white py-3 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all`}
                  data-testid="forgot-reset-password-btn"
                >
                  {loading ? (<><Loader2 className="animate-spin" size={20} />Resetting Password...</>) : (<><CheckCircle size={20} />Reset Password</>)}
                </button>
              </form>
            )}
          </div>
        </div>

        <p className="text-center text-gray-500 mt-6 text-sm">
          Remember your password?{' '}
          <Link to={config.loginPath} className={`${accent.linkBold} font-semibold`}>
            Login here
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
